import React, { FC, ReactNode, useCallback, useState } from 'react';
import { AssetContext, AssetContextState, DEFAULT_ASSET_STATE } from './AssetContext';
import { AssetState, AssetStatus } from 'src/types';
import { AssetType } from '@amzn/fire-tv-campaign-automation-service';

/**
 * Props for the AssetContextProvider component
 */
interface AssetContextProviderProps {
  /**
   * Child components that will have access to the asset context
   */
  children: ReactNode;

  /**
   * Optional initial state to override the default asset states,
   * this will be useful during cookie based state restoration.
   * @example
   * {
   *   [AssetType.LOGO]: {
   *     status: AssetStatus.COMPLETED,
   *     key: '/logo_images/logo_filename.png'
   *   }
   * }
   */
  initialState?: Partial<AssetContextState>;
}

/**
 * Provider component for managing asset states in the campaign automation form.
 * Provides a centralized way to manage and update the state of different assets
 * (background image, logo, video trailer) throughout the application.
 *
 * @example
 * // Basic usage
 * <AssetContextProvider>
 *   <App />
 * </AssetContextProvider>
 *
 * @example
 * // With initial state
 * const initialState = {
 *   [AssetType.LOGO]: {
 *     status: AssetStatus.COMPLETED,
 *     key: '/logo_images/logo_filename.png'
 *   }
 * };
 *
 * <AssetContextProvider initialState={initialState}>
 *   <App />
 * </AssetContextProvider>
 */
export const AssetContextProvider: FC<AssetContextProviderProps> = ({ children, initialState }) => {
  /**
   * State containing the current status and data for all assets.
   * Initialized with DEFAULT_ASSET_STATE and optionally overridden with initialState.
   */
  const [assetStates, setAssetStates] = useState<AssetContextState>({
    ...DEFAULT_ASSET_STATE,
    ...initialState,
  });

  /**
   * Updates the state of a specific asset type by merging the new state
   * with the existing state for that asset.
   *
   * @param assetType - The type of asset to update (e.g., BACKGROUND_IMAGE, LOGO)
   * @param state - Partial state to merge with the existing asset state
   * @throws Error if an invalid asset type is provided
   *
   * @example
   * // Update asset status
   * updateAssetState(AssetType.LOGO, { status: AssetStatus.PENDING_UPLOAD });
   *
   * @example
   * // Update asset URL and status
   * updateAssetState(AssetType.BACKGROUND_IMAGE, {
   *   status: AssetStatus.COMPLETED,
   *   key: '/bgd_images/bgd_filename.png'
   * });
   */
  const updateAssetState = useCallback((assetType: AssetType, state: Partial<AssetState>) => {
    // Validate that assetType is a valid key in DEFAULT_ASSET_STATE
    if (!(assetType in DEFAULT_ASSET_STATE)) {
      console.error(
        `Invalid asset type: ${assetType}. Must be one of: ${Object.keys(DEFAULT_ASSET_STATE).join(
          ', ',
        )}`,
      );
      return;
    }

    setAssetStates((prevStates) => ({
      ...prevStates,
      [assetType]: {
        ...prevStates[assetType],
        ...state,
      },
    }));
  }, []);

  return (
    <AssetContext.Provider value={{ assetStates, updateAssetState }}>
      {children}
    </AssetContext.Provider>
  );
};
